<script setup lang="ts">
// TODO - AF-539 This should be refactor to use FormTemplate
import { computed, onMounted, ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import type { CashDrawer, PaymentMethod } from '../../../../interfaces';
import type { Toast } from '@Atoms';
import { useADialog, useAToast } from '@Atoms';
import { ADOPT_URL, canSendSms } from '@Constants';
import { formatPhoneNumber, toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { getIntakeFlags } from '@AnimalModule';
import { checkDuplicatePersonService, emailValidation, phoneValidation } from '@FormModule';
import { useCartSteps } from '../../../../composables';
import { INTAKES_WITH_PAYMENT } from '../../../../constants';
import { CART_SEND_LINK_ERROR, CART_STEPS, PAYMENT_METHODS } from '../../../../enums';
import { isCheckInIntakeType } from '../../../../helpers';
import { SendCheckoutLinkService } from '../../../../services';
import useCartStore from '../../../../store';

const CONFIRM_WORD = 'Proceed';
let duplicateCheckRequestId = 0;

type DuplicateCheck = { field: 'phone' | 'email'; value: string };

const {
  cartData,
  cartId,
  cashDrawerOptions,
  isAdoption,
  isDonation,
  isIntake,
  isTransferOut,
  loading,
  outcomeType,
  people,
  pendingOutcomeRoute,
} = storeToRefs(useCartStore());
const { fetchCashDrawerOptions, outcomeCompletedCartReset } = useCartStore();
const { shelterProfile } = storeToRefs(useSessionStore());

const { notify, TOAST_TYPES } = useAToast();
const { activeModal, activeModalProps, dialogVisibility, SYSTEM_MODALS } = useADialog();
const { currentStep, previousStep } = useCartSteps();
const { push } = useRouter();
const { t } = useI18n();

const emits = defineEmits<{
  onStepUpdate: [CART_STEPS];
}>();

const isCheckInType = computed(() => isCheckInIntakeType(outcomeType.value));
// prettier-ignore
const checkoutOptionLabel = computed(() => isCheckInType.value ? 'cart.mobileCheckout.checkoutOptionCheckin'
  : isIntake.value ? 'cart.mobileCheckout.checkoutOptionIntake'
  : 'cart.mobileCheckout.checkoutOption');

const checkoutMethods = computed(() => [
  {
    icon: 'faMobile',
    // prettier-ignore
    label: isCheckInType.value ? 'cart.mobileCheckout.sendLinkCheckin'
      : isIntake.value ? 'cart.mobileCheckout.sendLinkIntake'
      : 'cart.mobileCheckout.sendLink',
    value: 'adopter',
  },
  {
    icon: 'faHome',
    // prettier-ignore
    label: isCheckInType.value ? 'cart.mobileCheckout.shelterDeviceCheckin'
      : isIntake.value? 'cart.mobileCheckout.shelterDeviceIntake'
      : 'cart.mobileCheckout.shelterDevice',
    value: 'shelter',
  },
]);

const deviceCheckoutMethods = [
  {
    icon: 'faWindowMaximize',
    label: 'cart.mobileCheckout.openTab',
    value: 'newTab',
  },
  {
    icon: 'faQrcode',
    label: 'cart.mobileCheckout.getQrCode',
    value: 'qrCode',
  },
  {
    icon: 'faLink',
    label: 'cart.mobileCheckout.sendLink',
    value: 'link',
  },
];

const cashDrawer = ref<CashDrawer | null>(null);
const adopterEmail = ref('');
const adopterEmailError = ref('');
const adopterPhone = ref('');
const adopterPhoneError = ref('');
const checkoutMethod = ref();
const deviceCheckoutMethod = ref('');
const paymentMethod = ref<PAYMENT_METHODS | null>(null);
const showErrorMessage = ref(false);
const receiptNotes = ref('');
const shelterDeviceSearchPhone = ref('');
const shelterDeviceSearchEmail = ref('');
const shelterDeviceSearchPhoneError = ref('');
const shelterDeviceSearchEmailError = ref('');
const duplicateResults = ref<any[]>([]);
const isCheckingDuplicate = ref(false);
const isSubmitting = ref(false);
const doNotAdoptConfirmed = ref(false);
const shelterDeviceSearchNoRecordFound = ref(false);
const adopterNoDoNotAdoptRecordFound = ref(false);

const adopterAppLink = computed(() => ADOPT_URL + '/mobile-checkout/' + cartId.value);
// NOTE :: Countries off the SMS list get the email path only; the api refuses a phone for them.
const isSmsAvailable = computed(() => canSendSms());
const isIntakeWithPayment = computed(() => INTAKES_WITH_PAYMENT.includes(cartData.value?.outcome_type));
const intakeHasPaymentEnabled = computed(() => getIntakeFlags(outcomeType.value, shelterProfile.value).payment);
const showCashDrawer = computed((): boolean =>
  // prettier-ignore
  isTransferOut.value ? false 
    : !isIntake.value ? !!shelterProfile.value?.cash_drawer
    : !!shelterProfile.value?.cash_drawer && isIntakeWithPayment.value && intakeHasPaymentEnabled.value
);
const showCheckoutLinkError = computed(
  () =>
    (checkoutMethod.value === 'adopter' && !adopterEmail.value && !adopterPhone.value) ||
    !!adopterPhoneError.value ||
    !!adopterEmailError.value
);
const showPaymentMethod = computed((): boolean =>
  // prettier-ignore
  isTransferOut.value ? false
    : !isIntake.value ? true 
    : isIntakeWithPayment.value && intakeHasPaymentEnabled.value
);

const hasDoNotAdoptPerson = computed(
  () => isAdoption.value && duplicateResults.value.some((person: any) => checkDoNotAdoptFlag(person))
);
const isSubmitBlockedByDoNotAdopt = computed(() => hasDoNotAdoptPerson.value && !doNotAdoptConfirmed.value);
const showFormErrors = computed(() => showErrorMessage.value && !isSubmitBlockedByDoNotAdopt.value);

const showShelterCheckoutMethod = computed(
  () => (checkoutMethod.value === 'shelter' && !deviceCheckoutMethod.value) || shelterCheckoutByLinkHasError.value
);

const shelterCheckoutByLinkHasError = computed(() => deviceCheckoutMethod.value === 'link' && !adopterEmail.value);

const isBelowMinimumAmount = computed(() => {
  const total = parseFloat(cartData.value?.total) || 0;
  const totalCents = toCents(total);
  const minimumCents = 50;

  return totalCents > 0 && totalCents < minimumCents;
});

const paymentOptionCreditCard = computed(() => {
  const disabledForEmptyTotal = !parseFloat(cartData.value?.total);
  const disableCreditCard = !shelterProfile.value?.stripe;
  const disabledForBelowMinimum = isBelowMinimumAmount.value;

  // prettier-ignore
  const message = disableCreditCard ? 'shelterInfo.settings.stripe.notIntegrated'
    : disabledForBelowMinimum ? 'cart.title.paymentMethodRequiresMinimumCreditCard'
    : 'cart.title.noCreditCardPaymentAvailable';

  return {
    icon: 'faCreditCard',
    key: PAYMENT_METHODS.CREDIT,
    label: 'cart.paymentOptions.creditCard',
    value: PAYMENT_METHODS.CREDIT,
    disabled: disabledForEmptyTotal || disableCreditCard || disabledForBelowMinimum,
    disabledMessage: message,
  };
});

const paymentOptions = computed((): PaymentMethod[] => [
  paymentOptionCreditCard.value,
  {
    icon: 'faCashRegister',
    key: PAYMENT_METHODS.OTHER,
    label: 'cart.paymentOptions.cashCheckOther',
    value: PAYMENT_METHODS.OTHER,
    disabled: false,
  },
]);

watch(adopterEmail, () => onEmailUpdate());
watch(adopterPhone, () => onPhoneUpdate());
watch([adopterEmailError, adopterPhone], () => onRequiredFieldsUpdate());
watch(isBelowMinimumAmount, () => onAmountValidation());
watch(checkoutMethod, () => onCheckoutMethodUpdate());
watch(shelterDeviceSearchPhone, () => onShelterDeviceSearchPhoneUpdate());
watch(shelterDeviceSearchEmail, () => onShelterDeviceSearchEmailUpdate());
watch(deviceCheckoutMethod, (val) => {
  if (val) showErrorMessage.value = false;
});

onMounted(async () => await onInitStep());

function checkDoNotAdoptFlag(person: any) {
  return !!person.do_not_adopt;
}

// NOTE :: The api refuses a link when one of the cart's animals is already held by another
// checkout (an open link, or a paid cart waiting to be completed). Translated here, not in the
// toast, because the text needs the animal's name filled in. The toast stays until dismissed: it
// explains why the Send button did nothing.
function getAnimalHeldToast(error: any): Toast | null {
  const data = error?.response?.data;
  if (data?.code !== CART_SEND_LINK_ERROR.ANIMAL_HAS_OPEN_CHECKOUT) return null;

  const holdKey = data.cart_status === 'paid' ? 'paid' : 'open';
  const animal = data.animal_name || t('cart.toast.animalHasOpenCheckout.fallbackAnimal');

  return {
    hasDimissButton: true,
    message: t(`cart.toast.animalHasOpenCheckout.${holdKey}.message`, { animal }),
    title: t('cart.toast.animalHasOpenCheckout.title'),
    type: TOAST_TYPES.WARNING,
  };
}

function resetDoNotAdoptState() {
  duplicateResults.value = [];
  doNotAdoptConfirmed.value = false;
  shelterDeviceSearchNoRecordFound.value = false;
}

function mergeDuplicateResults(resultSets: any[][]) {
  const byId = new Map<string, any>();

  for (const results of resultSets) {
    for (const person of results) {
      const personId = person.id || person._id?.$oid;

      if (!personId) continue;
      if (byId.has(personId)) continue;

      byId.set(personId, person);
    }
  }

  return Array.from(byId.values());
}

function getAdopterDeliveryDuplicateChecks(): DuplicateCheck[] {
  const checks: DuplicateCheck[] = [];
  const phone = adopterPhone.value?.trim();
  const email = adopterEmail.value?.trim();

  if (phone && !adopterPhoneError.value) {
    checks.push({ field: 'phone', value: formatPhoneNumber(phone) || phone });
  }
  if (email && !adopterEmailError.value) {
    checks.push({ field: 'email', value: email.toLowerCase() });
  }

  return checks;
}

function getShelterVerifyDuplicateChecks(): DuplicateCheck[] {
  const checks: DuplicateCheck[] = [];
  const phone = shelterDeviceSearchPhone.value?.trim();
  const email = shelterDeviceSearchEmail.value?.trim();

  if (phone && !shelterDeviceSearchPhoneError.value) {
    checks.push({ field: 'phone', value: formatPhoneNumber(phone) || phone });
  }
  if (email && !shelterDeviceSearchEmailError.value) {
    checks.push({ field: 'email', value: email.toLowerCase() });
  }

  return checks;
}

function getDuplicateChecksForSubmit(): DuplicateCheck[] {
  if (checkoutMethod.value === 'adopter') return getAdopterDeliveryDuplicateChecks();
  if (checkoutMethod.value === 'shelter' && isAdoption.value) return getShelterVerifyDuplicateChecks();

  return [];
}

function runDuplicateChecks(checks: DuplicateCheck[], options?: { source: 'shelter' }) {
  duplicateCheckRequestId++;
  const currentId = duplicateCheckRequestId;

  duplicateResults.value = [];
  doNotAdoptConfirmed.value = false;
  shelterDeviceSearchNoRecordFound.value = false;
  adopterNoDoNotAdoptRecordFound.value = false;
  isCheckingDuplicate.value = true;

  const requests = checks.map(({ field, value }) =>
    checkDuplicatePersonService({
      entity: 'people',
      field,
      value,
      return_do_not_adopt: true,
    })
  );

  return Promise.all(requests)
    .then((responses) => {
      if (currentId !== duplicateCheckRequestId) return;

      const merged = mergeDuplicateResults(responses.map((result) => (Array.isArray(result?.data) ? result.data : [])));
      duplicateResults.value = merged;

      const noDoNotAdoptFound = !merged.some((person) => checkDoNotAdoptFlag(person));
      if (options?.source === 'shelter') return (shelterDeviceSearchNoRecordFound.value = noDoNotAdoptFound);

      adopterNoDoNotAdoptRecordFound.value = noDoNotAdoptFound;
    })
    .finally(() => {
      if (currentId === duplicateCheckRequestId) isCheckingDuplicate.value = false;
    });
}

function onShelterDeviceSearchPhoneUpdate() {
  shelterDeviceSearchNoRecordFound.value = false;
  resetDoNotAdoptState();
  showErrorMessage.value = false;

  // prettier-ignore
  !shelterDeviceSearchPhone.value ? (shelterDeviceSearchPhoneError.value = '')
    : !phoneValidation(shelterDeviceSearchPhone.value) ? (shelterDeviceSearchPhoneError.value = 'components.forms.errors.validPhone')
    : (shelterDeviceSearchPhoneError.value = '');
}

function onShelterDeviceSearchEmailUpdate() {
  shelterDeviceSearchNoRecordFound.value = false;
  resetDoNotAdoptState();
  showErrorMessage.value = false;

  // prettier-ignore
  !shelterDeviceSearchEmail.value ? (shelterDeviceSearchEmailError.value = '')
    : !emailValidation(shelterDeviceSearchEmail.value) ? (shelterDeviceSearchEmailError.value = 'components.forms.errors.validEmail')
    : (shelterDeviceSearchEmailError.value = '');
}

function openDoNotAdoptModal() {
  activeModalProps.value = {
    onConfirm: () => {
      doNotAdoptConfirmed.value = true;
      showErrorMessage.value = false;
      submitCheckout();
    },
    results: duplicateResults.value,
  };
  activeModal.value = SYSTEM_MODALS.CART_DO_NOT_ADOPT_CONFIRM;
  dialogVisibility.value = true;
}

function onCheckoutMethodUpdate() {
  adopterEmail.value = '';
  adopterPhone.value = '';
  shelterDeviceSearchPhone.value = '';
  shelterDeviceSearchEmail.value = '';
  adopterEmailError.value = '';
  adopterPhoneError.value = '';
  shelterDeviceSearchPhoneError.value = '';
  shelterDeviceSearchEmailError.value = '';
  showErrorMessage.value = false;
  adopterNoDoNotAdoptRecordFound.value = false;
  resetDoNotAdoptState();

  if (checkoutMethod.value === 'adopter' && people.value) {
    const { email, phone } = people.value;

    if (email) adopterEmail.value = email;
    if (phone && isSmsAvailable.value) adopterPhone.value = phone;
  }
}

// EVENTS
async function onInitStep() {
  currentStep.value = CART_STEPS.MOBILE_CHECKOUT;

  loading.value = true;

  await fetchCashDrawerOptions();

  loading.value = false;
}

function onRequiredFieldsUpdate() {
  if (adopterEmailError.value && adopterEmailError.value) {
    showErrorMessage.value = showCheckoutLinkError.value && showShelterCheckoutMethod.value;
  }
}

function validateContactFields() {
  onEmailUpdate();
  onPhoneUpdate();
  onShelterDeviceSearchPhoneUpdate();
  onShelterDeviceSearchEmailUpdate();
}

function onEmailUpdate() {
  resetDoNotAdoptState();
  showErrorMessage.value = false;
  if (!adopterEmail.value) return (adopterEmailError.value = '');

  const notValidEmail = !emailValidation(adopterEmail.value);
  if (notValidEmail) return (adopterEmailError.value = 'components.forms.errors.validEmail');

  adopterEmailError.value = '';
}

function onPhoneUpdate() {
  resetDoNotAdoptState();
  showErrorMessage.value = false;
  if (!adopterPhone.value) return (adopterPhoneError.value = '');

  const notValidPhone = !phoneValidation(adopterPhone.value);
  if (notValidPhone) return (adopterPhoneError.value = 'components.forms.errors.validPhone');

  adopterPhoneError.value = '';
}

function onAmountValidation() {
  if (isBelowMinimumAmount.value && paymentMethod.value === PAYMENT_METHODS.CREDIT) {
    paymentMethod.value = null;
  }
}

function onNextStep() {
  if (isCheckingDuplicate.value) return;

  if (isSubmitBlockedByDoNotAdopt.value) {
    showErrorMessage.value = true;
    openDoNotAdoptModal();
    return;
  }

  validateContactFields();

  const paymentMethodError = !showPaymentMethod.value ? false : !paymentMethod.value;
  const hasError =
    paymentMethodError || !checkoutMethod.value || showCheckoutLinkError.value || showShelterCheckoutMethod.value;
  if (hasError) return (showErrorMessage.value = true);

  if (showCashDrawer.value && !cashDrawer.value) return (showErrorMessage.value = true);

  showErrorMessage.value = false;

  if (!isAdoption.value || doNotAdoptConfirmed.value) return submitCheckout();

  const checks = getDuplicateChecksForSubmit();
  if (!checks.length) return submitCheckout();

  const checkSource = checkoutMethod.value === 'shelter' ? { source: 'shelter' as const } : undefined;

  runDuplicateChecks(checks, checkSource).then(() => {
    if (hasDoNotAdoptPerson.value) {
      showErrorMessage.value = true;
      openDoNotAdoptModal();
      return;
    }

    submitCheckout();
  });
}

function submitCheckout() {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  loading.value = true;

  // NOTE: This is a expected setting in the adopter app
  // prettier-ignore
  const paymentType = !showPaymentMethod.value ? null
    : paymentMethod.value === PAYMENT_METHODS.CREDIT ? paymentMethod.value 
    : PAYMENT_METHODS.CASH;

  const email =
    checkoutMethod.value === 'shelter' && deviceCheckoutMethod.value !== 'link'
      ? shelterDeviceSearchEmail.value
      : adopterEmail.value;
  const typedPhone =
    checkoutMethod.value === 'shelter' && deviceCheckoutMethod.value !== 'link'
      ? shelterDeviceSearchPhone.value
      : adopterPhone.value;
  // NOTE :: The shelter-device phone is still a Do Not Adopt lookup there, but it is never sent to be texted.
  const phone = isSmsAvailable.value ? typedPhone : '';

  const PAYLOAD: any = {
    _id: cartId.value,
    cart_id: cartId.value,
    cart_type: cartData.value?.cart_type,
    checkoutFlow: checkoutMethod.value,
    email,
    payment_type: paymentType,
    phone,
    show_application: null, // NOTE: There is no feature using this for now
    show_contract: null, // NOTE: There is no feature using this for now
    status: 'pending',
  };

  if (showCashDrawer.value) {
    PAYLOAD.cash_drawer = cashDrawer.value?.label;
    PAYLOAD.cash_drawer_id = cashDrawer.value?._id.$oid;
  }

  if (receiptNotes.value) {
    PAYLOAD.receipt_notes = receiptNotes.value;
  }

  if (doNotAdoptConfirmed.value) {
    PAYLOAD.staff_confirmed_use_of_do_not_adopt_person = true;
  }

  const TOAST_ERROR = {
    message: 'errors.generics.unknownIssue.message',
    title: 'errors.generics.unknownIssue.title',
    type: TOAST_TYPES.ERROR,
  };

  SendCheckoutLinkService(PAYLOAD)
    .then(() => {
      // NOTE :: TS compliance - pendingOutcomeRoute should always exist when sending checkout link
      if (!pendingOutcomeRoute.value) {
        Sentry.captureException(new Error('pendingOutcomeRoute is null during onSendCheckoutLink'), {
          tags: { component: 'MCartStepMobileCheckout', type: 'ts-compliance' },
        });
      }

      if (deviceCheckoutMethod.value === 'newTab') window.open(adopterAppLink.value, '_blank');

      // NOTE :: Keep loading/isSubmitting locked through navigation. The router push is async, and
      // resetting these on `finally` re-enables the button before it finishes, letting a second
      // click fire a duplicate checkout against the already-reset cart.
      push(pendingOutcomeRoute.value!).then(() => outcomeCompletedCartReset());
    })
    .catch((error) => {
      notify(getAnimalHeldToast(error) ?? TOAST_ERROR);
      loading.value = false;
      isSubmitting.value = false;
    });
}
</script>

<template>
  <div class="flex h-fit flex-col gap-4">
    <main class="flex flex-1 flex-col gap-4">
      <MCashDrawer
        v-if="showCashDrawer"
        v-model="cashDrawer"
        :cashDrawerError="showFormErrors && showCashDrawer && !cashDrawer"
        :cashDrawerOptions
        :loading
        @onChange="showErrorMessage = false"
      />

      <!-- Payment Method -->
      <div
        v-if="showPaymentMethod"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showFormErrors && !paymentMethod"
          :label="$t('cart.mobileCheckout.paymentMethod')"
        />

        <div class="flex flex-wrap gap-1 sm:gap-4">
          <AFieldCheckboxButton
            v-model="paymentMethod"
            :options="paymentOptions"
          />
        </div>
      </div>

      <!-- Checkout Method -->
      <div class="flex flex-col gap-2">
        <AFieldLabel
          required
          :error="showFormErrors && !checkoutMethod"
          :label="$t(checkoutOptionLabel)"
        />

        <div class="flex flex-wrap gap-1 sm:gap-4">
          <AFieldCheckboxButton
            v-model="checkoutMethod"
            :options="checkoutMethods"
          />
        </div>
      </div>

      <!-- Send Link Options -->
      <div
        v-if="checkoutMethod === 'adopter'"
        class="flex flex-col gap-2"
      >
        <AFieldLabel
          required
          :error="showFormErrors && showCheckoutLinkError"
          :label="$t('cart.mobileCheckout.deliveryMethod')"
        />

        <div class="flex w-96 max-w-full flex-col justify-center gap-1">
          <template v-if="isSmsAvailable">
            <div class="flex flex-col items-start">
              <AFieldPhone
                v-model="adopterPhone"
                :debounceTime="0"
                :error="(showFormErrors && showCheckoutLinkError) || (adopterPhoneError && !adopterEmail)"
                :placeholder="$t('cart.mobileCheckout.enterPhone')"
              />

              <AFieldError
                v-if="adopterPhoneError && !adopterEmail"
                :error="adopterPhoneError"
              />
            </div>

            <div class="flex flex-col items-center">
              <span class="text-sm font-semibold">
                {{ $t('common.or') }}
              </span>
            </div>
          </template>

          <form class="flex flex-col items-start">
            <AFieldEmail
              v-model="adopterEmail"
              :debounceTime="0"
              :error="(showFormErrors && showCheckoutLinkError) || (adopterEmailError && !adopterPhone)"
              :placeholder="$t('cart.mobileCheckout.enterEmail')"
            />

            <AFieldError
              v-if="adopterEmailError && !adopterPhone"
              :error="adopterEmailError"
            />
          </form>

          <p
            v-if="!isSmsAvailable"
            class="text-sm text-gray-500"
          >
            {{ $t('components.forms.smsNotAvailableInCountry') }}
          </p>

          <div
            v-if="isAdoption && adopterNoDoNotAdoptRecordFound"
            class="flex items-center gap-2 text-sm font-medium text-green-900"
          >
            <i class="pi pi-check" />

            <p>{{ $t('cart.mobileCheckout.noDoNotAdoptRecordFound') }}</p>
          </div>
        </div>
      </div>

      <!-- Shelter Device Options -->
      <div
        v-if="checkoutMethod === 'shelter'"
        class="flex flex-col gap-2"
      >
        <div
          v-if="isAdoption"
          class="mb-3 flex max-w-[500px] flex-col gap-2"
        >
          <AFieldLabel :label="$t('cart.mobileCheckout.verifyDoNotAdoptFlag')" />

          <div class="flex flex-col gap-1">
            <AFieldPhone
              v-model="shelterDeviceSearchPhone"
              :debounceTime="0"
              :error="shelterDeviceSearchPhoneError"
              :placeholder="$t('cart.mobileCheckout.checkClientPhone')"
            />

            <AFieldError :error="shelterDeviceSearchPhoneError" />

            <AFieldEmail
              v-model="shelterDeviceSearchEmail"
              :debounceTime="0"
              :error="shelterDeviceSearchEmailError"
              :placeholder="$t('cart.mobileCheckout.checkClientEmail')"
            />

            <AFieldError :error="shelterDeviceSearchEmailError" />

            <div
              v-if="shelterDeviceSearchNoRecordFound"
              class="flex items-center gap-2 text-sm font-medium text-green-900"
            >
              <i class="pi pi-check" />

              <p>{{ $t('cart.mobileCheckout.noDoNotAdoptRecordFound') }}</p>
            </div>
          </div>
        </div>

        <AFieldLabel
          required
          :error="showFormErrors && showShelterCheckoutMethod"
          :label="$t('cart.mobileCheckout.deviceCheckoutMethod')"
        />

        <div class="flex flex-wrap gap-4">
          <AFieldCheckboxButton
            v-model="deviceCheckoutMethod"
            class="text-nowrap"
            :options="deviceCheckoutMethods"
          />
        </div>

        <div
          v-if="deviceCheckoutMethod === 'link'"
          class="max-w-[500px]"
        >
          <AFieldEmail
            v-model="adopterEmail"
            :debounceTime="0"
            :error="(showFormErrors && showShelterCheckoutMethod) || adopterEmailError"
            :placeholder="$t('cart.mobileCheckout.enterEmail')"
          />

          <AFieldError :error="adopterEmailError" />
        </div>
      </div>

      <!-- Receipt Notes -->
      <div
        v-if="!isDonation && !isTransferOut"
        class="flex max-w-[500px] flex-col gap-2"
      >
        <AFieldLabel :label="$t('cart.label.receiptNotes')" />

        <AFieldTextArea v-model="receiptNotes" />
      </div>
    </main>

    <footer class="flex-shrink">
      <Message
        v-if="showErrorMessage && !isSubmitBlockedByDoNotAdopt"
        pt:text="text-sm"
        severity="error"
        :closable="false"
      >
        {{ $t('cart.mobileCheckout.formError') }}
      </Message>

      <Message
        v-else-if="showErrorMessage && isSubmitBlockedByDoNotAdopt"
        pt:text="text-sm"
        severity="error"
        :closable="false"
      >
        {{ $t('cart.doNotAdopt.typeToConfirm', { word: CONFIRM_WORD }) }}
      </Message>

      <div class="flex flex-wrap gap-1 sm:justify-between">
        <Button
          outlined
          class="af-button af-button-success-outlined w-fit"
          icon="pi pi-arrow-left"
          :label="$t('common.back')"
          :loading
          @click="emits('onStepUpdate', previousStep)"
        />

        <Button
          class="af-button af-button-success sm:ml-auto"
          iconPos="right"
          :disabled="isCheckingDuplicate || isSubmitting"
          :label="$t('cart.label.submitAndComplete')"
          :loading="isSubmitting || loading || isCheckingDuplicate"
          @click="onNextStep"
        >
          <template #icon>
            <FaIcon
              class="mr-2"
              :icon="$icons.faPaw"
            />
          </template>
        </Button>
      </div>
    </footer>
  </div>
</template>
